import { BusinessError } from "@services/businessError";

/**
 * The axios success interceptor now rejects with a BusinessError when the backend returns a 2xx
 * response whose envelope reports failure (isSuccess:false). The user-facing error toast is
 * already dispatched by the interceptor, and callers' success side-effects are correctly skipped
 * because the promise rejects. Many call sites, however, are `mutation().then(onSuccess)` with no
 * `.catch` — for a BusinessError that produces a browser "Unhandled promise rejection" console
 * error, which is noise (the error was already surfaced to the user).
 *
 * This guard swallows ONLY BusinessError rejections (the interceptor already handled them),
 * leaving every other rejection to surface normally so real programming errors stay visible.
 *
 * Register once at app startup.
 */
export function registerBusinessErrorGuard(target: Pick<Window, "addEventListener"> = window): void {
  target.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    if (isBusinessError(event.reason)) {
      // Already toasted by the interceptor; prevent the console "Unhandled rejection" noise.
      event.preventDefault();
    }
  });
}

function isBusinessError(reason: unknown): boolean {
  return reason instanceof BusinessError || (typeof reason === "object" && reason !== null && (reason as { isBusinessError?: unknown }).isBusinessError === true);
}
